import type { ReactNode } from 'react'
import {
  IconeAdicionar,
  IconeCena,
  IconeConstrucao,
  IconeDesfazer,
  IconeDestravado,
  IconeDuplicar,
  IconeEditar,
  IconeEscalar,
  IconeEstabilizacao,
  IconeEstadoClinico,
  IconeExportar,
  IconeFerramenta,
  IconeGirar,
  IconeMover,
  IconeOficinaCorte,
  IconePistaAsfalto,
  IconeRefazer,
  IconeRemover,
  IconeRestaurar,
  IconeTravado,
} from '../icones'
import type { ModoTabletop } from '../tabletop/useTabletop'
import type { FerramentasModulo } from './tipos'

export type FerramentaGizmo = 'translate' | 'rotate' | 'scale'

export interface PropsTabletop {
  secaoAtiva?: 'cenario' | 'vitima' | 'corte' | 'estabilizacao'
  aoAtivarSecao?: (secao: 'cenario' | 'vitima' | 'corte' | 'estabilizacao') => void
  painelRoteiroAula?: (secao: 'cenario' | 'vitima' | 'corte' | 'estabilizacao') => ReactNode
  modoRoteiroAula?: boolean
  aoAlternarModoRoteiro?: () => void
  asfaltoAtivo?: boolean
  aoAlternarAsfalto?: () => void
  /** Cenário travado: só equipamentos se movem no arraste; veículos e obstáculos ficam fixos. */
  cenarioTravado?: boolean
  aoAlternarTravaCenario?: () => void
  painelAdicionar: () => ReactNode

  painelFerramentas: () => ReactNode
  painelFila: () => ReactNode
  painelObjetos: () => ReactNode
  painelCena: () => ReactNode
  painelIo: () => ReactNode
  painelEditor: () => ReactNode
  painelAtendimento?: () => ReactNode
  encaixeAtivo: boolean
  aoAlternarEncaixe: () => void
  quantidadeObjetos: number
  quantidadeFila: number
  quantidadeVitimas?: number
  /** Montar: bandeja/gizmo livres, todas as abas. Atender: cena travada, dock só com Atendimento/Cena. */
  modo: ModoTabletop
  aoDefinirModo: (modo: ModoTabletop) => void
  ferramenta: FerramentaGizmo
  aoEscolherFerramenta: (ferramenta: FerramentaGizmo) => void
  desfazer: () => void
  refazer: () => void
  podeDesfazer: boolean
  podeRefazer: boolean
  objetoSelecionado: {
    id: string
    nome: string
    tipo: string
    posicao: readonly [number, number, number]
    rotacaoGraus: number
    escala: number
    oculto: boolean
    bloqueado: boolean
    aoDuplicar: () => void
    aoRestaurar: () => void
    aoRemover: () => void
  } | null
  aoLimparSelecao: () => void
}

const GIZMOS: ReadonlyArray<{ id: FerramentaGizmo; rotulo: string; Icone: typeof IconeMover }> = [
  { id: 'translate', rotulo: 'Mover', Icone: IconeMover },
  { id: 'rotate', rotulo: 'Girar', Icone: IconeGirar },
  { id: 'scale', rotulo: 'Escalar', Icone: IconeEscalar },
]

/**
 * Table top: abas independentes para cenário, vítima, corte e estabilização; ferramentas de
 * montagem livres acessíveis em modo de edição de maquete.
 */
export function ferramentasTabletop(p: PropsTabletop): FerramentasModulo {
  const objeto = p.objetoSelecionado

  // Ramo prioritário: Roteiro Didático de Aula
  if (p.painelRoteiroAula) {
    return {
      grupos: [
        { id: 'adicionar', rotulo: 'Adicionar', rotuloCurto: 'Novo', icone: <IconeAdicionar />, conteudo: p.painelAdicionar },
        { id: 'cenario', rotulo: 'Cenário', icone: <IconeCena />, conteudo: () => p.painelRoteiroAula!('cenario'), ativa: p.secaoAtiva === 'cenario', aoAtivar: () => p.aoAtivarSecao?.('cenario') },
        { id: 'vitima', rotulo: 'Vítima', icone: <IconeEstadoClinico />, conteudo: () => p.painelRoteiroAula!('vitima'), ativa: p.secaoAtiva === 'vitima', aoAtivar: () => p.aoAtivarSecao?.('vitima') },
        { id: 'corte', rotulo: 'Corte', icone: <IconeOficinaCorte />, conteudo: () => p.painelRoteiroAula!('corte'), ativa: p.secaoAtiva === 'corte', aoAtivar: () => p.aoAtivarSecao?.('corte') },
        { id: 'estabilizacao', rotulo: 'Estabilização', rotuloCurto: 'Estab.', icone: <IconeEstabilizacao />, conteudo: () => p.painelRoteiroAula!('estabilizacao'), ativa: p.secaoAtiva === 'estabilizacao', aoAtivar: () => p.aoAtivarSecao?.('estabilizacao') },
        { id: 'editor', rotulo: 'Editar elemento', rotuloCurto: 'Editar', icone: <IconeEditar />, conteudo: p.painelEditor },
        {
          id: 'io',
          rotulo: 'Importar/Exportar',
          rotuloCurto: 'Arquivo',
          icone: <IconeExportar />,
          conteudo: () => <>{p.painelCena()}{p.painelIo()}</>,
        },
      ],
      chips: [
        { id: 'objetos', rotulo: 'Objetos', valor: p.quantidadeObjetos, tom: 'info' },
      ],
      acoesRapidas: (
        <>
          {p.aoAlternarAsfalto && (
            <button
              type="button"
              className={`btn-acao-cad ${p.asfaltoAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoAlternarAsfalto}
              aria-pressed={p.asfaltoAtivo}
              title={p.asfaltoAtivo ? 'Ocultar pista de asfalto (mostrar apenas grid neutro)' : 'Exibir pista de asfalto com faixas e acostamento'}
            >
              <IconePistaAsfalto /><span className="btn-acao-cad__rotulo">{p.asfaltoAtivo ? 'Pista de Asfalto' : 'Grid Neutro'}</span>
            </button>
          )}
          {p.aoAlternarTravaCenario && (
            <button
              type="button"
              className={`btn-acao-cad ${p.cenarioTravado ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              onClick={p.aoAlternarTravaCenario}
              aria-pressed={p.cenarioTravado}
              title={p.cenarioTravado
                ? 'Cenário travado: veículos e obstáculos fixos. Clique para destravar.'
                : 'Travar cenário: fixa veículos e obstáculos; só os equipamentos se movem.'}
            >
              {p.cenarioTravado ? <IconeTravado /> : <IconeDestravado />}
              <span className="btn-acao-cad__rotulo">{p.cenarioTravado ? 'Cenário Travado' : 'Travar Cenário'}</span>
            </button>
          )}
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            disabled={!p.podeDesfazer}
            onClick={p.desfazer}
            title="Desfazer (Ctrl+Z)"
          >
            <IconeDesfazer /><span className="btn-acao-cad__rotulo">Desfazer</span>
          </button>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            disabled={!p.podeRefazer}
            onClick={p.refazer}
            title="Refazer (Ctrl+Shift+Z)"
          >
            <IconeRefazer /><span className="btn-acao-cad__rotulo">Refazer</span>
          </button>
        </>
      ),
      inspetor: null,
    }
  }

  const botoesGizmo = (incluirEscala: boolean) =>
    GIZMOS.filter((g) => incluirEscala || g.id !== 'scale').map(({ id, rotulo, Icone }) => (
      <button
        key={id}
        type="button"
        className={`btn-acao-cad ${p.ferramenta === id ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
        onClick={() => p.aoEscolherFerramenta(id)}
        aria-pressed={p.ferramenta === id}
        title={`${rotulo} objeto`}
      >
        <Icone />{rotulo}
      </button>
    ))

  const emAtendimento = p.modo === 'atender'

  const gruposCompletos = [
    { id: 'adicionar', rotulo: 'Adicionar', rotuloCurto: 'Novo', icone: <IconeAdicionar />, conteudo: p.painelAdicionar },
    {
      id: 'ferramentas',
      rotulo: 'Ferramentas',
      icone: <IconeFerramenta />,
      conteudo: p.painelFerramentas,
      // só tem Desfazer/Refazer, que no celular já flutuam sobre a cena
      somenteDesktop: true,
    },
    ...(p.painelAtendimento
      ? [
          {
            id: 'atendimento',
            rotulo: 'Atendimento',
            rotuloCurto: 'Vítimas',
            icone: <IconeEstadoClinico />,
            badge: p.quantidadeVitimas,
            conteudo: p.painelAtendimento,
          },
        ]
      : []),
    {
      id: 'objetos',
      rotulo: 'Objetos',
      icone: <IconeConstrucao />,
      badge: p.quantidadeObjetos,
      conteudo: p.painelObjetos,
    },
    { id: 'cena', rotulo: 'Cena', icone: <IconeCena />, conteudo: p.painelCena },
    {
      id: 'io',
      rotulo: 'Importar/Exportar',
      rotuloCurto: 'Arquivo',
      icone: <IconeExportar />,
      conteudo: p.painelIo,
    },
    {
      id: 'editor',
      rotulo: 'Editor',
      icone: <IconeConstrucao />,
      desabilitado: !objeto,
      conteudo: p.painelEditor,
    },
  ]

  // Em Atender a turma só precisa acompanhar o atendimento e o nome/estado da cena
  const IDS_DOCK_ATENDER = new Set(['atendimento', 'cena'])
  const grupos = emAtendimento
    ? gruposCompletos.filter((g) => IDS_DOCK_ATENDER.has(g.id))
    : gruposCompletos.filter((g) => g.id !== 'atendimento')

  return {
    grupos,
    chips: [
      { id: 'objetos', rotulo: 'Objetos', valor: p.quantidadeObjetos, tom: 'info' },
    ],
    acoesRapidas: (
      <>
        {p.aoAlternarModoRoteiro && (
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--destaque"
            onClick={p.aoAlternarModoRoteiro}
            title="Voltar às abas da Aula"
          >
            <IconeCena />Voltar ao Roteiro
          </button>
        )}
        {emAtendimento && <div className="tt-alternador-modo" role="group" aria-label="Sessão antiga do Table top">
          <button
            type="button"
            className={`btn-acao-cad ${!emAtendimento ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            aria-pressed={!emAtendimento}
            onClick={() => p.aoDefinirModo('montar')}
            title="Montar a cena: bandeja de peças e arraste livres"
          >
            <IconeConstrucao />Montar
          </button>
          <button
            type="button"
            className={`btn-acao-cad ${emAtendimento ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            aria-pressed={emAtendimento}
            onClick={() => p.aoDefinirModo('atender')}
            title="Atender a ocorrência: cena travada, toque numa vítima abre o Atendimento"
          >
            <IconeEstadoClinico />Atender
          </button>
        </div>}
        {!emAtendimento && (
          <>
            {botoesGizmo(false)}
            <button type="button"
              className={`btn-acao-cad ${p.encaixeAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
              aria-pressed={p.encaixeAtivo}
              onClick={p.aoAlternarEncaixe}
              title="Alinhar posições a cada 25 cm">
              Encaixe 25 cm
            </button>
          </>
        )}
        {p.aoAlternarAsfalto && (
          <button
            type="button"
            className={`btn-acao-cad ${p.asfaltoAtivo ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
            onClick={p.aoAlternarAsfalto}
            aria-pressed={p.asfaltoAtivo}
            title={p.asfaltoAtivo ? 'Ocultar pista de asfalto (mostrar apenas grid neutro)' : 'Exibir pista de asfalto com faixas e acostamento'}
          >
            <IconePistaAsfalto /><span className="btn-acao-cad__rotulo">{p.asfaltoAtivo ? 'Pista de Asfalto' : 'Grid Neutro'}</span>
          </button>
        )}
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--secundario"
          disabled={!p.podeDesfazer}
          onClick={p.desfazer}
          title="Desfazer (Ctrl+Z)"
        >
          <IconeDesfazer /><span className="btn-acao-cad__rotulo">Desfazer</span>
        </button>
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--secundario"
          disabled={!p.podeRefazer}
          onClick={p.refazer}
          title="Refazer (Ctrl+Shift+Z)"
        >
          <IconeRefazer /><span className="btn-acao-cad__rotulo">Refazer</span>
        </button>
      </>
    ),
    // Propriedades/gizmo de edição não fazem sentido com a cena travada em Atender.
    inspetor: emAtendimento ? null : objeto && {
      titulo: objeto.nome,
      subtitulo: objeto.tipo,
      propriedades: [
        {
          rotulo: 'Posição (m)',
          valor: objeto.posicao.map((n) => n.toFixed(2)).join(' · '),
        },
        { rotulo: 'Rotação', valor: `${objeto.rotacaoGraus}°` },
        { rotulo: 'Escala', valor: `${objeto.escala.toFixed(2)}×` },
        { rotulo: 'Visibilidade', valor: objeto.oculto ? 'oculto' : 'visível' },
        { rotulo: 'Bloqueio', valor: objeto.bloqueado ? 'travado' : 'livre' },
      ],
      acoes: (
        <>
          <div className="inspetor__linha-controle">{botoesGizmo(true)}</div>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={objeto.aoDuplicar}
          >
            <IconeDuplicar />Duplicar
          </button>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--secundario"
            onClick={objeto.aoRestaurar}
          >
            <IconeRestaurar />Restaurar transformação
          </button>
          <button
            type="button"
            className="btn-acao-cad btn-acao-cad--perigo"
            onClick={objeto.aoRemover}
          >
            <IconeRemover />Remover
          </button>
        </>
      ),
      aoFechar: p.aoLimparSelecao,
    },
  }
}

